import React from "react";
import PropTypes from "prop-types";
import Tooltip from "react-tooltip";
import c from "classnames";
import withMetrics from "../withMetrics";
import styles from "./tooltip.module.sass";

export {
  TableTipIcon,
};

function TableTipIcon({ children, className, id, text }) {
  const tip = withMetrics(({metrics}) => (
    <TooltipWrapper id={id} metrics={metrics} text={text}>
      {children}
    </TooltipWrapper>
  ));
  return (
    <span
      className={c("tooltip", className)}
      aria-label={text}
    >
      {React.createElement(tip)}
    </span>
  );
}

function buildTipProps(isMobile, tipProps) {
  return isMobile
    ? Object.assign({}, tipProps, { globalEventOff: "click touch" })
    : tipProps;
}

function buildCloneProps(isMobile, tipId) {
  const base = { "data-for": tipId, "data-tip": true };
  if (isMobile) {
    return Object.assign({}, base, { "data-event": "click touch" });
  } else {
    return base;
  }
}

/**
 * Tooltip component that reacts to client's screen size.
 *
 * Props:
 *  - children
 *  - className
 *  - id: needs to be unique, as it affects where the tooltip is displayed
 *  - metrics: used for mobile design reactivity
 *  - text
 *  - place: One of: top, top-start, top-end, right, right-start,
 *    right-end, bottom, bottom-start, bottom-end, left,
 *    left-start or left-end
 *
 * == Example: render an icon with a tooltip
 *
 *   <TooltipWrapper id="myicon" text="This button will trigger an action">
 *     <i className="fas fa-envelope"></i>
 *   </TooltipWrapper>
 */
function TooltipWrapper(props) {
  const {
    children,
    className,
    id,
    metrics,
    place,
    text,
    ...origTipProps
  } = props;
  const tipId = id + "-tip";
  const isMobile = metrics.window.width < 481;
  const tipProps = buildTipProps(isMobile, origTipProps);
  const cloneProps = buildCloneProps(isMobile, tipId);

  return (
    <React.Fragment>
      <Tooltip
        className={styles.tooltip}
        place={place || "top"}
        effect={"solid"}
        id={tipId}
        backgroundColor="#000000"
        {...tipProps}
      >
        {text}
      </Tooltip>
      {children ? React.cloneElement(children, cloneProps) : null}
    </React.Fragment>
  );
}

TooltipWrapper.propTypes = {
  afterShow: PropTypes.func,
  id: PropTypes.string.isRequired,
  metrics: PropTypes.object.isRequired,
  place: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
};

export default withMetrics(TooltipWrapper);
